import React, { useEffect, useState } from "react";
import { deleteRequest, getRequest, postRequest, postFile } from "../../apiWrapper";
import { toast } from "react-toastify";

// サンプル: 講師と生徒のリストも propsやAPIで取得可能な前提
const AdminLessonScheduleSnapshots = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [diffTarget1, setDiffTarget1] = useState("");
  const [diffTarget2, setDiffTarget2] = useState("");
  const [diffType, setDiffType] = useState("teacher"); // "teacher" or "student"
  const [teacherList, setTeacherList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [subjectList, setSubjectList] = useState([]);
  const [targetPerson, setTargetPerson] = useState(""); // 講師IDや生徒ID
  const [diffResult, setDiffResult] = useState(null);

  useEffect(() => {
    fetchSnapshots();
    fetchTeachers();
    fetchStudents();
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const res = await getRequest("subjects");
      setSubjectList(res.subjects || []);
    } catch (err) {
      toast.error("科目一覧の取得に失敗しました");
    }
  };

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const res = await getRequest("lessonScheduleSnapshots");
      setSnapshots(res.snapshots || []);
    } catch (err) {
      toast.error("スナップショット一覧取得失敗");
    }
    setLoading(false);
  };

  const fetchTeachers = async () => {
    try {
      const res = await getRequest("teachers?excludeRetired=true");
      setTeacherList(res.teachers || []);
    } catch (err) {
      toast.error("講師一覧の取得に失敗しました");
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await getRequest("students?excludeWithdrawn=true");
      setStudentList(res.students || []);
    } catch (err) {
      toast.error("生徒一覧の取得に失敗しました");
    }
  };

  const handleTakeSnapshot = async () => {
    setLoading(true);
    try {
      const res = await postRequest("lessonScheduleSnapshots");
      toast.success("スナップショットを取得しました");
      fetchSnapshots();
    } catch (err) {
      toast.error("スナップショット取得に失敗しました");
    }
    setLoading(false);
  };

  const handleRestore = async (id) => {
    if (!window.confirm("このスナップショットで復元しますか？（直前の状態は自動でバックアップされます）")) return;
    setRestoringId(id);
    try {
      // 1. 復元前に現状スナップショットを自動取得
      await postRequest("lessonScheduleSnapshots");
      // 2. 指定IDで復元
      await postRequest(`lessonScheduleSnapshots/${id}/restore`);
      toast.success("復元が完了しました");
      fetchSnapshots();
    } catch (err) {
      toast.error("復元に失敗しました");
    }
    setRestoringId(null);
  };

  // ...stateなどは省略...

  const handleDeleteSnapshot = async (id) => {
    if (!window.confirm("本当にこのスナップショットを削除しますか？")) return;
    setLoading(true);
    try {
      const res = await deleteRequest(`lessonScheduleSnapshots/${id}`);
      if (!res.ok) throw new Error();
      toast.success("削除しました");
      fetchSnapshots(); // 一覧を更新
    } catch (err) {
      toast.error("削除に失敗しました");
    }
    setLoading(false);
  };

  const handleDiff = async () => {
    if (!diffTarget1 || !diffTarget2 || !targetPerson) {
      toast.error("比較対象・講師/生徒を選択してください");
      return;
    }
    setDiffResult(null);
    setLoading(true);
    try {
      // 差分比較APIにPOST
      const res = await postRequest("lessonScheduleSnapshots/compare", {
        snapshotIdA: diffTarget1,
        snapshotIdB: diffTarget2,
        mode: diffType,        // "teacher" or "student"
        targetId: targetPerson // 講師IDまたは生徒ID
      });
      const data = await res.json();
      setDiffResult(data.diff || []);
    } catch (err) {
      toast.error("差分取得に失敗しました");
    }
    setLoading(false);
  };

const handleExportExcel = async (snapshotId, templateFile) => {
  if (!templateFile) {
    toast.error("テンプレートファイルを選択してください");
    return;
  }

  const formData = new FormData();
  formData.append("templateFile", templateFile);

  try {
    const res = await postFile(`lessonScheduleSnapshots/${snapshotId}/exportExcel`, formData);

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "エクスポートに失敗しました");
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "レッスン提案書.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success("エクスポートが完了しました");
  } catch (err) {
    console.error("エクスポート失敗:", err);
    toast.error(err.message || "エクスポート中にエラーが発生しました");
  }
};



  return (
    <div className="p-4 pt-20">
      <h1 className="text-2xl font-bold mb-6">レッスンスケジュール スナップショット管理</h1>

      {/* --- スナップショット操作バー --- */}
      <div className="flex gap-4 mb-8 flex-wrap">
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={handleTakeSnapshot}
          disabled={loading}
        >
          スナップショットを取得
        </button>
      </div>

      {/* --- スナップショット一覧 --- */}
      <div className="mb-10">
        <h2 className="text-lg font-bold mb-3">履歴一覧</h2>
        <table className="min-w-max border">
          <thead>
            <tr>
              <th className="p-2 border">日時</th>
              <th className="p-2 border">レコード数</th>
              <th className="p-2 border"></th>
            </tr>
          </thead>
          <tbody>
            {snapshots.map(snap => (
              <tr key={snap.id}>
                <td className="p-2 border">{new Date(snap.snapshot_at).toLocaleString()}</td>
                <td className="p-2 border">{snap.record_count}</td>
                <td className="p-2 border">
                  <button
                    className="bg-yellow-500 text-white px-2 py-1 rounded mr-2"
                    onClick={() => handleRestore(snap.id)}
                    disabled={restoringId === snap.id}
                  >
                    復元
                  </button>
                  <button
                    className="bg-red-600 text-white px-2 py-1 rounded"
                    onClick={() => handleDeleteSnapshot(snap.id)}
                    disabled={loading}
                  >
                    削除
                  </button>
                  <label className="bg-blue-600 text-white px-2 py-1 rounded cursor-pointer">
                    Excel出力
                    <input
                      type="file"
                      accept=".xlsx"
                      style={{ display: "none" }}
                      onChange={(e) => handleExportExcel(snap.id, e.target.files[0])}
                    />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>

        </table>
      </div>

      {/* --- 差分比較UI --- */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-3">スナップショット差分比較</h2>
        <div className="flex items-center gap-4 flex-wrap mb-4">
          <div>
            <span>比較A:</span>
            <select value={diffTarget1} onChange={e => setDiffTarget1(e.target.value)}>
              <option value="">選択</option>
              {snapshots.map(snap => (
                <option key={snap.id} value={snap.id}>{new Date(snap.snapshot_at).toLocaleString()}</option>
              ))}
            </select>
          </div>
          <div>
            <span>比較B:</span>
            <select value={diffTarget2} onChange={e => setDiffTarget2(e.target.value)}>
              <option value="">選択</option>
              {snapshots.map(snap => (
                <option key={snap.id} value={snap.id}>{new Date(snap.snapshot_at).toLocaleString()}</option>
              ))}
            </select>
          </div>
          <div>
            <select value={diffType} onChange={e => setDiffType(e.target.value)}>
              <option value="teacher">講師で比較</option>
              <option value="student">生徒で比較</option>
            </select>
            <select
              value={targetPerson}
              onChange={e => setTargetPerson(e.target.value)}
            >
              <option value="">選択</option>
              {(diffType === "teacher" ? teacherList : studentList).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded"
            onClick={handleDiff}
            disabled={loading}
          >
            差分を表示
          </button>
        </div>
        {/* 差分テーブル */}
        {diffResult && (
          <DiffResultTable
            diff={diffResult}
            teacherList={teacherList}
            studentList={studentList}
            subjectList={subjectList}
          />
        )}
      </div>
    </div>
  );
};

// --- 差分結果テーブル例 ---
function DiffResultTable({ diff, teacherList, studentList, subjectList }) {
  // ID→名前の変換用関数
  const getTeacherName = id => teacherList.find(t => t.id === id)?.name || id || "";
  const getStudentName = id => studentList.find(s => s.id === id)?.name || id || "";
  const getSubjectName = id => subjectList.find(s => s.id === id)?.name || id || "";

  // レコードの内容を人が読める形に変換
  const renderCell = (record) => {
    if (!record) return "";
    return (
      <div>
        <div>講師: {getTeacherName(record.teacherid)}</div>
        <div>
          生徒1: {getStudentName(record.student1id)}（{getSubjectName(record.subject1id)}）
        </div>
        <div>
          生徒2: {getStudentName(record.student2id)}（{getSubjectName(record.subject2id)}）
        </div>
      </div>
    );
  };

  if (!diff || diff.length === 0) return <div>差分はありません。</div>;
  return (
    <table className="min-w-max border text-xs">
      <thead>
        <tr>
          <th className="p-1 border">種別</th>
          <th className="p-1 border">日付</th>
          <th className="p-1 border">コマ</th>
          <th className="p-1 border">前</th>
          <th className="p-1 border">後</th>
        </tr>
      </thead>
      <tbody>
        {diff.map((d, i) => (
          <tr key={i}>
            <td className="p-1 border">{d.type}</td>
            <td className="p-1 border">{d.before?.date || d.after?.date}</td>
            <td className="p-1 border">{d.before?.period || d.after?.period}</td>
            <td className="p-1 border">{renderCell(d.before)}</td>
            <td className="p-1 border">{renderCell(d.after)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default AdminLessonScheduleSnapshots;
